import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  ArrowLeft,
  CalendarDays,
  Clock3,
  MapPin,
  Users,
  CarFront,
  CreditCard,
  ShieldCheck,
  User,
  Mail,
  Phone,
  LockKeyhole,
} from "lucide-react";
import { createQuote, BookingData } from "./BookingService";
import type { BookingPageData } from "./BookingPage";
import "./BookingSummaryProps.css";

const API_BASE_URL = process.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  throw new Error("REACT_APP_API_URL is not defined");
}

const stripePublishableKey =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

const stripePromise = loadStripe(stripePublishableKey);

type PaymentData = {
  bookingId: string;
  amount?: number;
  currency?: string;
  clientSecret: string;
};

type BookingSummaryData = BookingPageData & {
  bookingId?: string;
  amount?: number;
  currency?: string;
  pickupLabel?: string;
  destinationLabel?: string;
};

function InlineCheckoutForm({
  bookingId,
  amount,
  currency,
}: {
  bookingId: string;
  amount?: number;
  currency?: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);

  const formattedAmount = useMemo(() => {
    if (typeof amount !== "number") return null;

    return new Intl.NumberFormat(i18n.language === "pt" ? "pt-PT" : "en-GB", {
      style: "currency",
      currency: (currency || "EUR").toUpperCase(),
    }).format(amount);
  }, [amount, currency, i18n.language]);

  const handlePayment = async () => {
    if (!stripe || !elements || !isPaymentElementReady) {
      setErrorMessage(t("bookingSummary.errors.paymentLoading"));
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/success`,
        },
        redirect: "if_required",
      });

      if (error) {
        navigate("/payment/success", {
          state: {
            bookingId,
            amount,
            currency,
            paymentStatus: "failed",
            message: error.message || t("bookingSummary.errors.paymentFailed"),
          },
        });
        return;
      }

      navigate("/payment/success", {
        state: {
          bookingId,
          amount,
          currency,
          paymentStatus: paymentIntent?.status || "succeeded",
        },
      });
    } catch (err) {
      console.error("Erro no checkout:", err);

      navigate("/payment/success", {
        state: {
          bookingId,
          amount,
          currency,
          paymentStatus: "failed",
          message: t("bookingSummary.errors.paymentUnexpected"),
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="inline-checkout">
      <div className="inline-checkout-header">
  <div>
    <h3>{t("bookingSummary.checkout.title")}</h3>
    <p>{t("bookingSummary.checkout.subtitle")}</p>
  </div>
</div>

      <div className="inline-checkout-secure">
        <LockKeyhole size={15} />
        <span>{t("bookingSummary.checkout.secure")}</span>
      </div>

      <div className="inline-payment-element">
        <PaymentElement onReady={() => setIsPaymentElementReady(true)} />
      </div>

      {errorMessage && (
        <div className="booking-payment-error">{errorMessage}</div>
      )}

      <button
        type="button"
        className="booking-primary-btn"
        onClick={handlePayment}
        disabled={isLoading || !stripe || !elements || !isPaymentElementReady}
      >
        {isLoading
          ? t("bookingSummary.checkout.processing")
          : isPaymentElementReady
          ? t("bookingSummary.checkout.payNow")
          : t("bookingSummary.checkout.loading")}
      </button>
    </div>
  );
}

export function BookingSummary() {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { t, i18n } = useTranslation();

  const [travelData, setTravelData] = useState<BookingSummaryData | null>(null);
  const [isLoadingBooking, setIsLoadingBooking] = useState(true);

  const [paymentType, setPaymentType] = useState<"na-hora" | "agora">(
    "na-hora"
  );

  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    async function loadBooking() {
      try {
        if (!bookingId) {
          navigate("/booking");
          return;
        }

        const res = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}`, {
          credentials: "include",
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || t("bookingSummary.errors.invalidBooking"));
        }

        setTravelData(data);
      } catch (err) {
        console.error("Erro ao carregar reserva:", err);
        navigate("/booking");
      } finally {
        setIsLoadingBooking(false);
      }
    }

    loadBooking();
  }, [bookingId, navigate, t]);

  const formattedTotal = useMemo(() => {
    if (typeof travelData?.amount !== "number") return null;

    return new Intl.NumberFormat(i18n.language === "pt" ? "pt-PT" : "en-GB", {
      style: "currency",
      currency: (travelData.currency || "EUR").toUpperCase(),
    }).format(travelData.amount);
  }, [travelData?.amount, travelData?.currency, i18n.language]);

  const formatDate = (d?: Date | string | null) =>
    d
      ? new Date(d).toLocaleDateString(i18n.language === "pt" ? "pt-PT" : "en-GB")
      : "-";

  const formatLocation = (value?: string, label?: string) => {
    if (label?.trim()) return label;
    if (!value) return "-";

    const airportLabels: Record<string, string> = {
      faro: t("bookingSummary.airports.faro"),
      lisbon: t("bookingSummary.airports.lisbon"),
      seville: t("bookingSummary.airports.seville"),
    };

    return airportLabels[value] ?? value;
  };

  const formatVehicle = (vehicle?: string) => {
    if (!vehicle) return "-";

    const vehicleLabels: Record<string, string> = {
      standard: t("bookingSummary.vehicles.standard"),
      group: t("bookingSummary.vehicles.group"),
      "premium-group": t("bookingSummary.vehicles.premiumGroup"),
      sedan: t("bookingSummary.vehicles.sedan"),
      executive: t("bookingSummary.vehicles.executive"),
      van: t("bookingSummary.vehicles.van"),
      luxury: t("bookingSummary.vehicles.luxury"),
      minibus: t("bookingSummary.vehicles.minibus"),
    };

    return vehicleLabels[vehicle] ?? vehicle;
  };

  const buildBookingData = (): BookingData | null => {
    if (!travelData || !bookingId) return null;

    return {
      bookingId,
      pickup: travelData.pickup,
      destination: travelData.destination,
      tripDate: travelData.tripDate,
      time: travelData.time,
      returnDate: travelData.returnDate || "",
      returnTime: travelData.returnTime || "",
      passengers: travelData.passengers,
      adults: travelData.adults,
      children: travelData.children,
      babies: travelData.babies,
      vehicleType: travelData.vehicleType,
      specialRequests: travelData.specialRequests,
      name: travelData.leadName,
      email: travelData.email,
      contact: travelData.mobile,
      paymentType,
      resort: travelData.resort,
      country: travelData.country,
      foundUs: travelData.foundUs,
    };
  };

  const prepareOnlinePayment = async () => {
    if (!travelData) return;

    if (!stripePublishableKey) {
      setBookingError(
        t("bookingSummary.errors.missingStripeKey")
      );
      return;
    }

    if (paymentData?.clientSecret) return;

    setIsCreatingBooking(true);
    setBookingError("");

    try {
      const bookingData = buildBookingData();
      if (!bookingData) return;

      const result = await createQuote({
        ...bookingData,
        paymentType: "agora",
      });

      if (result.clientSecret) {
        setPaymentData({
          bookingId: result.bookingId,
          amount: result.amount,
          currency: result.currency,
          clientSecret: result.clientSecret,
        });

        setTimeout(() => {
          document
            .querySelector(".inline-checkout")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);

        return;
      }

      setBookingError(t("bookingSummary.errors.missingPaymentData"));
    } catch (err) {
      console.error(err);
      setBookingError(t("bookingSummary.errors.preparePayment"));
    } finally {
      setIsCreatingBooking(false);
    }
  };

  const handlePaymentTypeChange = async (value: "na-hora" | "agora") => {
    setPaymentType(value);
    setBookingError("");

    if (value === "agora") {
      await prepareOnlinePayment();
    }
  };

  const handleSubmit = async () => {
    if (!travelData) return;

    setIsCreatingBooking(true);
    setBookingError("");

    try {
      const bookingData = buildBookingData();
      if (!bookingData) return;

      const result = await createQuote({
        ...bookingData,
        paymentType: "na-hora",
      });

      if (result) {
        navigate("/payment/success", {
          state: {
            bookingId: result.bookingId,
            amount: result.amount,
            currency: result.currency,
            paymentStatus: "pay_on_arrival",
          },
        });
      }
    } catch (err) {
      console.error(err);
      setBookingError(t("bookingSummary.errors.createBooking"));
    } finally {
      setIsCreatingBooking(false);
    }
  };

  if (isLoadingBooking) {
    return (
      <div className="booking-page">
        <div className="booking-shell">
          <div className="booking-empty">
            <h2>{t("bookingSummary.states.loadingBooking")}</h2>
          </div>
        </div>
      </div>
    );
  }

  if (!travelData) {
    return (
      <div className="booking-page">
        <div className="booking-shell">
          <div className="booking-empty">
            <h2>{t("bookingSummary.header.badge")}</h2>
            <p>{t("bookingSummary.states.notFound")}</p>

            <button
              type="button"
              className="booking-secondary-btn"
              onClick={() => navigate("/booking")}
            >
              {t("bookingSummary.actions.back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const travelDetails = [
    {
      icon: <MapPin size={18} />,
      label: t("bookingSummary.travel.pickup"),
      value: formatLocation(travelData.pickup, travelData.pickupLabel),
    },
    {
      icon: <MapPin size={18} />,
      label: t("bookingSummary.travel.destination"),
      value: formatLocation(travelData.destination, travelData.destinationLabel),
    },
    {
      icon: <CalendarDays size={18} />,
      label: t("bookingSummary.travel.date"),
      value: formatDate(travelData.tripDate),
    },
    {
      icon: <Clock3 size={18} />,
      label: t("bookingSummary.travel.time"),
      value: travelData.time || "-",
    },
    {
      icon: <Users size={18} />,
      label: t("bookingSummary.travel.passengers"),
      value: `${travelData.passengers || 0}`,
    },
    {
      icon: <CarFront size={18} />,
      label: t("bookingSummary.travel.vehicle"),
      value: formatVehicle(travelData.vehicleType),
    },
  ];

  return (
    <div className="booking-page">
      <div className="booking-shell">
        <div className="booking-topbar">
          <button
            type="button"
            className="booking-back-inline"
            onClick={() => navigate("/booking")}
          >
            <ArrowLeft size={18} />
            <span>{t("bookingSummary.actions.back")}</span>
          </button>
        </div>

        <div className="booking-layout">
          <aside className="booking-sidebar">
            <div className="booking-sidebar-card">
              <div className="booking-badge">{t("bookingSummary.header.badge")}</div>

              <h1>{t("bookingSummary.header.title")}</h1>

              <p className="booking-subtitle">
                {t("bookingSummary.header.subtitle")}
              </p>

              <div className="booking-section">
                <div className="section-heading">
                  <h2>{t("bookingSummary.sections.travelDetails")}</h2>
                </div>

                <div className="summary-list">
                  {travelDetails.map((item) => (
                    <div className="summary-item" key={item.label}>
                      <div className="summary-item-icon">{item.icon}</div>

                      <div className="summary-item-content">
                        <span className="summary-item-label">{item.label}</span>

                        <strong className="summary-item-value">
                          {item.value}
                        </strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {travelData.specialRequests?.trim() && (
                <div className="booking-note">
                  <span>{t("bookingSummary.specialRequest")}</span>
                  <p>{travelData.specialRequests}</p>
                </div>
              )}
            </div>
          </aside>

          <main className="booking-main">
            <div className="booking-main-card">
              <section className="booking-section">
                <div className="section-heading">
                  <h2>{t("bookingSummary.sections.yourDetails")}</h2>
                  <p>{t("bookingSummary.sections.yourDetailsSubtitle")}</p>
                </div>

                <div className="form-grid">
                  <div className="input-group">
                    <label>{t("bookingSummary.customer.fullName")}</label>

                    <div className="locked-field">
                      <User size={16} />
                      <span>{travelData.leadName || "-"}</span>
                    </div>
                  </div>

                  <div className="input-group">
                    <label>{t("bookingSummary.customer.email")}</label>

                    <div className="locked-field">
                      <Mail size={16} />
                      <span>{travelData.email || "-"}</span>
                    </div>
                  </div>

                  <div className="input-group input-group--full">
                    <label>{t("bookingSummary.customer.contact")}</label>

                    <div className="locked-field">
                      <Phone size={16} />
                      <span>{travelData.mobile || "-"}</span>
                    </div>
                  </div>
                </div>
              </section>

              <section className="booking-section">
                <div className="section-heading">
                  <h2>{t("bookingSummary.sections.payment")}</h2>
                  <p>{t("bookingSummary.sections.paymentSubtitle")}</p>
                </div>

                <div className="booking-total-preview">
                  <div>
                    <span>{t("bookingSummary.total.title")}</span>
                    <p>
                      {t("bookingSummary.total.description")}
                    </p>
                  </div>

                  <strong>{formattedTotal || "-"}</strong>
                </div>

                <div className="payment-cards">
                  <button
                    type="button"
                    className={`payment-card ${
                      paymentType === "na-hora" ? "active" : ""
                    }`}
                    onClick={() => handlePaymentTypeChange("na-hora")}
                    disabled={isCreatingBooking}
                  >
                    <div className="payment-card-header">
                      <div className="payment-card-icon">
                        <ShieldCheck size={18} />
                      </div>

                      <div className="payment-card-radio" />
                    </div>

                    <div className="payment-card-body">
                      <strong>{t("bookingSummary.payment.payOnArrival.title")}</strong>
                      <span>
                        {t("bookingSummary.payment.payOnArrival.description")}
                      </span>
                    </div>
                  </button>

                  <button
                    type="button"
                    className={`payment-card ${
                      paymentType === "agora" ? "active" : ""
                    }`}
                    onClick={() => handlePaymentTypeChange("agora")}
                    disabled={isCreatingBooking}
                  >
                    <div className="payment-card-header">
                      <div className="payment-card-icon">
                        <CreditCard size={18} />
                      </div>

                      <div className="payment-card-radio" />
                    </div>

                    <div className="payment-card-body">
                      <strong>{t("bookingSummary.payment.payNow.title")}</strong>
                      <span>
                        {t("bookingSummary.payment.payNow.description")}
                      </span>
                    </div>
                  </button>
                </div>
              </section>

              {bookingError && (
                <div className="booking-payment-error">{bookingError}</div>
              )}

              {paymentType === "na-hora" && (
                <div className="booking-actions">
                  <button
                    type="button"
                    className="booking-primary-btn"
                    onClick={handleSubmit}
                    disabled={isCreatingBooking}
                  >
                    {isCreatingBooking
                      ? t("bookingSummary.actions.creatingBooking")
                      : t("bookingSummary.actions.confirmBooking")}
                  </button>
                </div>
              )}

              {paymentType === "agora" && isCreatingBooking && (
                <div className="booking-loading-payment">
                  {t("bookingSummary.states.preparingPayment")}
                </div>
              )}

              {paymentType === "agora" && paymentData && (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: paymentData.clientSecret,
                    appearance: {
                      theme: "stripe",
                      variables: {
                        colorPrimary: "#0f766e",
                        colorBackground: "#ffffff",
                        colorText: "#0f172a",
                        colorDanger: "#dc2626",
                        fontFamily:
                          "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        spacingUnit: "4px",
                        borderRadius: "12px",
                      },
                      rules: {
                        ".Input": {
                          border: "1px solid #e2e8f0",
                          boxShadow: "none",
                          padding: "13px 14px",
                        },
                        ".Input:focus": {
                          border: "1px solid #0f766e",
                          boxShadow: "0 0 0 3px rgba(15, 118, 110, 0.08)",
                        },
                        ".Tab": {
                          border: "1px solid #e2e8f0",
                          backgroundColor: "#ffffff",
                          boxShadow: "none",
                        },
                        ".Tab--selected": {
                          borderColor: "#0f766e",
                          backgroundColor: "#f0fdfa",
                          boxShadow: "none",
                        },
                        ".Label": {
                          color: "#475569",
                          fontWeight: "500",
                        },
                      },
                    },
                  }}
                >
                  <InlineCheckoutForm
                    bookingId={paymentData.bookingId}
                    amount={paymentData.amount}
                    currency={paymentData.currency}
                  />
                </Elements>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}